import { cn } from "@repo/ui/lib/utils";
import { SidebarInset } from "@repo/ui/ui/sidebar";
import type { PropsWithChildren } from "react";

/**
 * Shell for any authenticated page (and the read-only board viewer). Wraps
 * `SidebarInset`, which is styled by the sibling `<Sidebar variant="inset">`
 * via `peer-data-*` selectors. Pass `standalone` when rendered without a
 * sidebar peer (read-only public boards) so we replicate the same rounded
 * card chrome explicitly.
 */
const Content = ({
  children,
  standalone = false,
}: PropsWithChildren<{ standalone?: boolean }>) => {
  return (
    <SidebarInset
      className={cn(
        "min-h-0 overflow-hidden md:peer-data-[variant=inset]:mt-0",
        standalone &&
          "md:m-2 md:mt-0 md:rounded-xl md:border md:border-border md:shadow-sm",
      )}
    >
      {children}
    </SidebarInset>
  );
};

const ContentBody = ({ children }: PropsWithChildren) => {
  return (
    <div className="no-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="@container/main flex max-h-full flex-1 flex-col gap-2">
        {children}
      </div>
    </div>
  );
};

export const ContentHeader = ({
  children,
  className,
  style,
}: PropsWithChildren<{ className?: string; style?: React.CSSProperties }>) => {
  return (
    <header
      className={cn(
        "flex h-(--header-height) shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)",
        className,
      )}
      style={style}
    >
      <div className="flex w-full items-center gap-2 px-2">{children}</div>
    </header>
  );
};

const ContentNamespace = Object.assign(Content, {
  Header: ContentHeader,
  Body: ContentBody,
});

export { ContentNamespace as Content };
